import { useInviteEligibilityQuery } from '@src/queries/InviteEligibilityQuery.generated'
import { InviteCoWorkerRoute } from '@src/routes'
import { useCurrentUser } from '@utils/useCurrentUser'
import { useUserSession } from '@utils/useUserSession'
import { getLocalizedUrl } from '@src/utils/regional'
import getConfig from 'next/config'
import { UserErpType } from '@src/types/graphql-types'
import countriesMap from '@localization/countries.json'
import { useCheckUserCountry } from './useCheckUserCountry'

interface ShowCoworkerConfig {
  shouldShowInviteCoworker: boolean
  inviteCoworkerUrl: string
}

const {
  publicRuntimeConfig: { featureFlags },
} = getConfig()

export const usePartnerInviteEligibility = (
  partner: any,
  email: string | undefined,
  userCountryCode: string,
  skip: boolean
) => {
  return useInviteEligibilityQuery({
    variables: {
      input: {
        salesOrg: partner?.salesOrg,
        soldTo: partner?.soldTo,
        email,
        soldToCountry: userCountryCode,
      },
    },
    skip,
  })
}

export const useShowInviteCoworker = (): ShowCoworkerConfig => {
  const {
    currentUser,
    singlePartners,
    inviteCoWorkerAffiliation,
    userErpType,
    isPurchasingAgent,
  } = useCurrentUser()

  const { userSession } = useUserSession()
  const inviteCoworkerUrl = getLocalizedUrl(
    userSession,
    InviteCoWorkerRoute.index()
  )

  const isChinaUser = useCheckUserCountry(countriesMap.CN.countryCode)
  const isBlueUser = userErpType === UserErpType.Blue
  const isPurpleUser = userErpType === UserErpType.Purple
  const isChinaPurpleUser = isPurpleUser && isChinaUser

  const partnerInfo =
    currentUser?.__typename === 'LoggedInUser'
      ? [...(currentUser.soldToPartners ?? [])]
      : []

  const defaultPartners = partnerInfo.filter((partner) => partner.isDefault)

  const userCountryCode =
    currentUser?.__typename === 'LoggedInUser'
      ? currentUser?.metadata?.country
      : ''

  const shouldQueryEligibility =
    featureFlags.inviteCoworker && currentUser?.email && userCountryCode
  const shouldSkipPurpleUserQueryEligibility =
    !shouldQueryEligibility ||
    !isChinaPurpleUser ||
    partnerInfo.length > 2 ||
    (isChinaPurpleUser && isPurchasingAgent)
  const shouldSkipRedOrBlueUserQueryEligibility =
    !shouldQueryEligibility || !singlePartners || isChinaPurpleUser

  const purpleUserQueryA = usePartnerInviteEligibility(
    defaultPartners[0],
    currentUser?.email,
    userCountryCode,
    shouldSkipPurpleUserQueryEligibility
  )

  const purpleUserQueryB = usePartnerInviteEligibility(
    defaultPartners[1],
    currentUser?.email,
    userCountryCode,
    shouldSkipPurpleUserQueryEligibility
  )

  const redOrBlueUserQuery = usePartnerInviteEligibility(
    defaultPartners[0],
    currentUser?.email,
    userCountryCode,
    shouldSkipRedOrBlueUserQueryEligibility
  )

  const isLoading = isChinaPurpleUser
    ? [purpleUserQueryA, purpleUserQueryB].some(({ loading }) => loading)
    : redOrBlueUserQuery.loading

  const partnerEligibilityCheck =
    !isLoading &&
    (isChinaPurpleUser
      ? [purpleUserQueryA, purpleUserQueryB].every(
          ({ data }) => !!data?.getInviteEligibility
        )
      : !!redOrBlueUserQuery.data?.getInviteEligibility)
  const bluePurpleUserCheck = () =>
    isBlueUser || isPurpleUser
      ? isChinaUser && !isPurchasingAgent && partnerInfo.length <= 2
      : singlePartners

  return {
    shouldShowInviteCoworker:
      featureFlags.inviteCoworker &&
      inviteCoWorkerAffiliation &&
      partnerEligibilityCheck &&
      bluePurpleUserCheck(),
    inviteCoworkerUrl: inviteCoworkerUrl.as,
  }
}
